import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseFeatureFlags } from '../../hooks/useFeatureFlag';

function buildSearchWithPersistedFlags(search, persistedFlags) {
  const params = new URLSearchParams(search || '');

  const parts = [];
  const seenKeysLower = new Set();

  for (const [key, value] of params.entries()) {
    const keyLower = key.toLowerCase();

    if (value === '') {
      parts.push(encodeURIComponent(key));
      seenKeysLower.add(keyLower);
      continue;
    }

    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    seenKeysLower.add(keyLower);
  }

  for (const flag of persistedFlags) {
    if (seenKeysLower.has(flag)) continue;
    parts.push(encodeURIComponent(flag));
  }

  if (parts.length === 0) return '';
  return `?${parts.join('&')}`;
}

export default function FeatureFlagPersistor() {
  const location = useLocation();
  const navigate = useNavigate();

  const persistedFlagsRef = useRef(new Set());

  useEffect(() => {
    const flagsInUrl = parseFeatureFlags(location.search);

    if (flagsInUrl.size > 0) {
      persistedFlagsRef.current = flagsInUrl;
      return;
    }

    const persistedFlags = persistedFlagsRef.current;
    if (persistedFlags.size === 0) return;

    const nextSearch = buildSearchWithPersistedFlags(location.search, persistedFlags);

    if (nextSearch !== location.search) {
      navigate(
        { pathname: location.pathname, search: nextSearch, hash: location.hash },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
}
